/**
 * Human "source" descriptions for workflow evidence items.
 *
 * An evidence item's `summary` is the raw tool transcript line —
 * `find_doc_sections with {"query":…} returned: [file.docx] doc=… chunk=…
 * (score=…) preview: …` — which reads as JSON noise in the UI. These helpers
 * turn each item into WHAT WAS CONSULTED: the document file name(s), the
 * historian tags queried, the P&ID sheet/file read — with an optional short
 * content preview. The Evidence UI renders these as "Sources".
 */

/**
 * A v2 step row's evidence entry — named fields, not a transcript sentence.
 *
 * The v1 engine writes `EvidencePackItem`, whose `summary` reads `"<tool> with {json} returned: …"`
 * and has to be taken apart again by `splitToolEcho` below. v2 writes what it means
 * (`workflows-v2/step-record.ts`), so these helpers read the fields directly and fall back to the
 * parsing path only for a v1 row.
 */
interface NativeEvidenceItem {
  tool?: unknown
  ask?: unknown
  answer?: unknown
  sources?: unknown
}

/** The v2 shape, recognised by the `ev-` id plus the pair no v1 item carries. */
function nativeItem(item: Record<string, unknown>): NativeEvidenceItem | null {
  return typeof item.id === 'string' &&
    item.id.startsWith('ev-') &&
    typeof item.tool === 'string' &&
    typeof item.answer === 'string'
    ? (item as NativeEvidenceItem)
    : null
}

/** `{ kind, ref, label? }` entries a v2 row carries, filtered to the readable ones. */
function nativeSources(item: NativeEvidenceItem): Array<{ kind: string; ref: string; label?: string }> {
  if (!Array.isArray(item.sources)) return []
  const out: Array<{ kind: string; ref: string; label?: string }> = []
  for (const raw of item.sources) {
    if (typeof raw !== 'object' || raw === null) continue
    const rec = raw as { kind?: unknown; ref?: unknown; label?: unknown }
    if (typeof rec.ref !== 'string' || rec.ref.trim() === '') continue
    out.push({
      kind: typeof rec.kind === 'string' ? rec.kind : '',
      ref: rec.ref,
      ...(typeof rec.label === 'string' && rec.label.trim() ? { label: rec.label } : {}),
    })
  }
  return out
}

export interface EvidenceSource {
  /** The consulted source(s): file name(s), historian tags, sheet, or a cleaned description. */
  label: string
  /** Short human preview of what came back (never raw JSON). */
  preview?: string
}

const MAX_NAMED = 4

/** `<tool> with {json} returned: rest` → its parts (all optional). */
function splitToolEcho(summary: string): { tool?: string; input?: string; rest: string } {
  const m = summary.match(/^([a-z_]+) with (\{[\s\S]*?\}) returned:\s*([\s\S]*)$/i)
  if (m) return { tool: m[1].toLowerCase(), input: m[2], rest: m[3] }
  const bare = summary.match(/^([a-z_]+) returned:\s*([\s\S]*)$/i)
  if (bare) return { tool: bare[1].toLowerCase(), rest: bare[2] }
  return { rest: summary }
}

/** Unique `[bracketed]` names in a doc-tool result — the searched files. */
function bracketFileNames(text: string): string[] {
  const names: string[] = []
  for (const match of text.matchAll(/\[([^\][]{2,120})\]/g)) {
    const name = match[1].trim()
    if (name && !names.includes(name)) names.push(name)
  }
  return names
}

function joinNamed(names: string[]): string {
  if (names.length <= MAX_NAMED) return names.join(', ')
  return `${names.slice(0, MAX_NAMED).join(', ')} +${names.length - MAX_NAMED} more`
}

/** The `preview:` tail (or the readable head) of a tool result, JSON stripped. */
function contentPreview(rest: string): string | undefined {
  const preview = rest.match(/preview:\s*([\s\S]{1,200})/i)?.[1] ?? rest
  const cleaned = preview
    .replace(/\[[^\][]*\]/g, ' ') // file brackets already surfaced as the label
    .replace(/\b(doc|chunk|score|chars)=[^\s,)]+[,)]?/gi, ' ')
    .replace(/[{}"|]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
  if (!cleaned || cleaned.length < 8) return undefined
  return cleaned.length > 140 ? `${cleaned.slice(0, 140)}…` : cleaned
}

/** Historian tag ids in a query string (X69PIC1125_pida_pv-style tokens). */
function historianTags(text: string): string[] {
  const tags: string[] = []
  for (const match of text.matchAll(/\b[A-Z][A-Z0-9]{2,}[A-Za-z0-9_]*_[A-Za-z0-9_]+\b/g)) {
    if (!tags.includes(match[0])) tags.push(match[0])
  }
  return tags
}

function parseInput(input?: string): Record<string, unknown> {
  if (!input) return {}
  try {
    const parsed = JSON.parse(input) as unknown
    return typeof parsed === 'object' && parsed !== null ? (parsed as Record<string, unknown>) : {}
  } catch {
    return {}
  }
}

/** One evidence item → a human source line (+ preview). */
export function describeEvidenceSource(item: Record<string, unknown>): EvidenceSource {
  // A v2 row STATES its sources; nothing has to be recovered from prose. Its `answer` is already
  // the readable result, so the preview needs no un-JSON-ing either.
  const native = nativeItem(item)
  if (native) {
    const sources = nativeSources(native)
    const answer = typeof native.answer === 'string' ? native.answer.trim() : ''
    const preview = answer ? (answer.length > 140 ? `${answer.slice(0, 140)}…` : answer) : undefined
    if (sources.length > 0) {
      const named = joinNamed(sources.map((s) => s.label ?? s.ref))
      const tagged = sources.every((s) => s.kind === 'tag')
      return { label: tagged ? `Historian — ${named}` : named, ...(preview ? { preview } : {}) }
    }
    const ask = typeof native.ask === 'string' ? native.ask.trim() : ''
    const label = ask
      ? `Knowledge graph — “${ask.length > 60 ? `${ask.slice(0, 60)}…` : ask}”`
      : typeof native.tool === 'string'
        ? native.tool
        : 'Retrieval'
    return { label, ...(preview ? { preview } : {}) }
  }

  const summary = typeof item.summary === 'string' ? item.summary.trim() : ''
  const kind = typeof item.kind === 'string' ? item.kind : ''
  const { tool, input, rest } = splitToolEcho(summary)
  const parsed = parseInput(input)

  // A model-written label (attached at evidence-record time for rows with no
  // extractable structure) wins over the fallback — but never over exact
  // file/tag extraction below, which continues first when structure exists.
  const modelLabel = typeof item.sourceLabel === 'string' && item.sourceLabel.trim() ? item.sourceLabel.trim() : null

  // Documents: the searched files are the source (one entry may span several).
  const files = bracketFileNames(rest)
  if (files.length > 0) {
    return { label: joinNamed(files), preview: contentPreview(rest) }
  }

  // Historian readings: the queried tags are the source.
  if (kind === 'historian' || tool === 'timeseries' || tool === 'query_historian') {
    const query = typeof parsed.query === 'string' ? parsed.query : summary
    const tags = historianTags(query)
    return {
      label: tags.length ? `Historian — ${joinNamed(tags)}` : 'Historian readings',
      preview: contentPreview(rest),
    }
  }

  // Direct file/sheet reads.
  const path = typeof parsed.path === 'string' ? parsed.path : typeof parsed.sheet === 'string' ? parsed.sheet : null
  if (path) {
    const base = path.split('/').filter(Boolean).pop() ?? path
    return { label: base, preview: contentPreview(rest) }
  }

  // KG navigation (grep/count/trace/glob/db_*): show what was searched for.
  const query =
    typeof parsed.pattern === 'string' ? parsed.pattern : typeof parsed.query === 'string' ? parsed.query : null
  if (tool && query) {
    return { label: `Knowledge graph — “${query.length > 60 ? `${query.slice(0, 60)}…` : query}”`, preview: contentPreview(rest) }
  }

  // Fallback chain: the model-written label; a display name older evidence
  // shapes carry (title/name/label); then the cleaned summary — never raw
  // tool JSON, and never a `workflow:…:tool:N` internal id.
  if (modelLabel) return { label: modelLabel, preview: contentPreview(rest) }
  const displayName = ['title', 'name', 'label']
    .map((key) => item[key])
    .find((value): value is string => typeof value === 'string' && value.trim().length > 0)
  if (displayName) return { label: displayName.trim(), preview: contentPreview(rest) }
  const cleaned = contentPreview(summary) ?? summary
  return { label: cleaned.length > 140 ? `${cleaned.slice(0, 140)}…` : cleaned || 'Source' }
}

/**
 * Deduped source labels across a step's evidence list (node inspector). Two
 * doc hits on the same file collapse to one name.
 */
export function evidenceSourceNames(evidence: unknown): string[] {
  if (!Array.isArray(evidence)) return []
  const names: string[] = []
  for (const raw of evidence) {
    if (typeof raw !== 'object' || raw === null) continue
    const { label } = describeEvidenceSource(raw as Record<string, unknown>)
    for (const part of label.split(', ')) {
      const name = part.replace(/\s*\+\d+ more$/, '').trim()
      if (name && !names.includes(name)) names.push(name)
    }
  }
  return names
}

/**
 * Document FILE NAMES a step's evidence actually touched (node inspector's
 * "Files searched" chips): bracket names from doc-tool results plus read
 * paths' basenames. Deduped, order of first appearance.
 */
export function evidenceSearchedFiles(evidence: unknown): string[] {
  if (!Array.isArray(evidence)) return []
  const names: string[] = []
  for (const raw of evidence) {
    if (typeof raw !== 'object' || raw === null) continue
    // v2 rows NAME what they consulted, by identity. A sheet key or doc id the tool reported is a
    // fact; the bracket scan below is a guess about prose, and it only ever saw what a result
    // happened to print.
    const native = nativeItem(raw as Record<string, unknown>)
    if (native) {
      for (const source of nativeSources(native)) {
        if (source.kind === 'tag') continue
        const name = source.label ?? source.ref
        if (!names.includes(name)) names.push(name)
      }
      continue
    }
    const summary = (raw as { summary?: unknown }).summary
    if (typeof summary !== 'string') continue
    const { input, rest } = splitToolEcho(summary.trim())
    for (const name of bracketFileNames(rest)) {
      if (!names.includes(name)) names.push(name)
    }
    const parsed = parseInput(input)
    const path = typeof parsed.path === 'string' ? parsed.path : null
    if (path) {
      const base = path.split('/').filter(Boolean).pop() ?? path
      if (!names.includes(base)) names.push(base)
    }
  }
  return names
}
